import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PlatformProvider, usePlatform } from './contexts/PlatformContext';
import { Toaster } from './components/ui/toaster';
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
import RightSidebar from './components/layout/RightSidebar';
import BusinessHub from './components/business/BusinessHub';
import LoginPage from './components/auth/LoginPage';
import LandingPage from './components/LandingPage';
import Home from './pages/Home';
import MyNetwork from './pages/MyNetwork';
import Jobs from './pages/Jobs';
import Messages from './pages/Messages';
import Notifications from './pages/Notifications';
import Profile from './pages/Profile';
import Company from './pages/Company';
import Groups from './pages/Groups';
import Events from './pages/Events';
import Opportunities from './pages/Opportunities';
import ProposalDetails from './pages/ProposalDetails';
import MessageCompose from './pages/MessageCompose';
import DigiTPro from './pages/DigiTPro';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import ForTrainers from './pages/ForTrainers';
import ForOrganizations from './pages/ForOrganizations';
import Pricing from './pages/Pricing';
import ContactUs from './pages/ContactUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import './App.css';

// Component to handle page animations for Social Network
const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <div key={location.pathname} className="page-enter">
      <Routes location={location}>
        <Route path="/" element={<Home />} />
        <Route path="/network" element={<MyNetwork />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/opportunities" element={<Opportunities />} />
        <Route path="/proposal/:proposalId" element={<ProposalDetails />} />
        <Route path="/digitpro" element={<DigiTPro />} />
        <Route path="/digitpro/:trainerId" element={<DigiTPro />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/messages/compose" element={<MessageCompose />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/company/:id" element={<Company />} />
        <Route path="/groups" element={<Groups />} />
        <Route path="/events" element={<Events />} />
        <Route path="/admin" element={<AdminDashboard />} />
      </Routes>
    </div>
  );
};

// Main app layout with authentication check
const AppLayout = () => {
  const { currentMode, toggleMode } = usePlatform();
  const { isAuthenticated, isLoading, user } = useAuth();
  const [showLanding, setShowLanding] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const location = window.location;

  // Check if current path is admin-login (hidden admin access)
  const isAdminLoginPath = location.pathname === '/admin-login';
  const isLoginPath = location.pathname === '/login';
  const isAdminRoute = location.pathname === '/admin';
  const isForTrainersRoute = location.pathname === '/for-trainers';
  const isForOrganizationsRoute = location.pathname === '/for-organizations';
  const isPricingRoute = location.pathname === '/pricing';
  const isContactRoute = location.pathname === '/contact';
  const isPrivacyRoute = location.pathname === '/privacy';
  const isTermsRoute = location.pathname === '/terms';
  
  // Redirect authenticated users away from login page
  // AND set correct mode based on user type
  useEffect(() => {
    if (isLoginPath && isAuthenticated && user) {
      // Set platform mode based on user type
      if (user.user_type === 'organization') {
        toggleMode('business');
        window.location.href = '/business';
      } else {
        // Trainers can choose, default to social
        window.location.href = '/';
      }
    }
  }, [isLoginPath, isAuthenticated, user, toggleMode]);
  
  // Show admin login page if on admin-login path (no authentication required to see the page)
  if (isAdminLoginPath) {
    return <AdminLogin />;
  }

  // Show login page if on /login path
  if (isLoginPath && !isAuthenticated) {
    return <LoginPage onBackToLanding={() => window.location.href = '/'} />;
  }

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading TrainerLinked.in...</p>
        </div>
      </div>
    );
  }

  // Show admin dashboard if on admin route (regardless of authentication for the component to handle)
  if (isAdminRoute) {
    return <AdminDashboard />;
  }

  // Show For Trainers page
  if (isForTrainersRoute) {
    return <ForTrainers />;
  }

  // Show For Organizations page
  if (isForOrganizationsRoute) {
    return <ForOrganizations />;
  }

  // Show Pricing page
  if (isPricingRoute) {
    return <Pricing />;
  }

  // Show Contact page
  if (isContactRoute) {
    return <ContactUs />;
  }

  // Show Privacy page
  if (isPrivacyRoute) {
    return <PrivacyPolicy />;
  }

  // Show Terms page
  if (isTermsRoute) {
    return <TermsOfService />;
  }

  // Show login page if user clicked get started
  if (!isAuthenticated && showLogin) {
    return <LoginPage onBackToLanding={() => { setShowLogin(false); setShowLanding(true); }} />;
  }

  // Show landing page if not authenticated and haven't clicked get started
  if (!isAuthenticated && showLanding) {
    return <LandingPage onGetStarted={() => { setShowLanding(false); setShowLogin(true); }} />;
  }

  // Show main app if authenticated
  return (
    <div className="min-h-screen bg-gray-50">
      {currentMode === 'social' && <Header />}
      {currentMode === 'social' ? (
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-6 py-6">
            <div className="col-span-2">
              <Sidebar />
            </div>
            <div className="col-span-7">
              <AnimatedRoutes />
            </div>
            <div className="col-span-3">
              <RightSidebar />
            </div>
          </div>
        </div>
      ) : (
        <BusinessHub />
      )}
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PlatformProvider>
          <div className="App">
            <AppLayout />
            <Toaster />
          </div>
        </PlatformProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;